<template>
  <div class="pt-2">
    <KbSkeletonRows
      v-if="captainLoading"
      :rows="2"
      aside
      label="Loading captains"
    />

    <template v-else>
      <div class="kb-list-head">
        <span class="kb-row-meta">
          <template v-if="!subkrewe.captains || subkrewe.captains.length === 0">No captains yet</template>
          <template v-else>{{ subkrewe.captains.length }} {{ subkrewe.captains.length === 1 ? 'captain' : 'captains' }}</template>
        </span>
        <Button
          v-if="!readOnly"
          :label="!subkrewe.captains || subkrewe.captains.length === 0 ? 'Assign captain' : 'Add captain'"
          icon="pi pi-plus"
          text
          size="small"
          @click="showAssignCaptainDialog = true"
        />
      </div>

      <ul
        v-if="subkrewe.captains && subkrewe.captains.length > 0"
        class="kb-rows m-0 p-0 list-none"
      >
        <li
          v-for="captain in subkrewe.captains"
          :key="captain.id"
          class="kb-row"
        >
          <div class="min-w-0">
            <button
              v-tooltip.top="{ value: 'View captain details', showDelay: 300 }"
              type="button"
              class="kb-row-title text-left hover:text-kb-accent hover:underline"
              @click="viewCaptainDetails(captain)"
            >
              <i class="pi pi-star-fill !text-[10px] mr-1.5 text-[color:var(--kb-type-captain)]" />{{ captain.name || 'Unnamed captain' }}
            </button>
            <p class="kb-row-meta m-0 mt-0.5 flex flex-wrap gap-x-2">
              <span class="whitespace-nowrap">
                <i
                  class="pi !text-[10px] mr-1"
                  :class="[duesNoteFor(captain).icon, `kb-dues-${duesNoteFor(captain).tone}`]"
                />{{ duesNoteFor(captain).label }}
              </span>
              <a
                v-if="captain.email"
                v-tooltip.top="{ value: 'Email', showDelay: 300 }"
                :href="`mailto:${captain.email}`"
                class="truncate hover:text-kb-accent hover:underline"
                @click.stop
              >{{ captain.email }}</a>
              <a
                v-if="captain.phone"
                v-tooltip.top="{ value: 'Call', showDelay: 300 }"
                :href="`tel:${captain.phone}`"
                class="hover:text-kb-accent hover:underline"
                @click.stop
              >{{ captain.phone }}</a>
            </p>
          </div>
          <div
            v-if="!readOnly"
            class="kb-row-aside"
          >
            <Button
              v-tooltip.top="{ value: 'Remove captain', showDelay: 300 }"
              label="Remove"
              text
              severity="danger"
              size="small"
              @click="removeCaptain(captain)"
            />
          </div>
        </li>
      </ul>
    </template>

    <!-- Dialog for assigning captain -->
    <AdminMemberSearchDialog
      v-if="!readOnly"
      :visible="showAssignCaptainDialog"
      title="Assign captain"
      search-label="Search for a member"
      placeholder="Name or email"
      :filter-function="filterPotentialCaptains"
      @update:visible="showAssignCaptainDialog = $event"
      @item-selected="selectCaptainAndAssign"
    />
  </div>
</template>

<script setup lang="ts">
import { markRaw, nextTick, ref } from 'vue'
import { AdminMemberDrawerContent } from '#components'
import type { Member } from '~/shared/types/member'
import type { SubKrewe } from '~/shared/types/subkrewe'
import { duesNote } from '~/utils/duesTag'
import { searchMembers } from '~/utils/memberSearch'

interface Props {
  subkrewe: SubKrewe
  readOnly?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['update'])

const subkrewesStore = useSubKrewesStore()
const membersStore = useMembersStore()
const toast = useToast()
const confirm = useConfirm()
const { setInternalNavigation, openDrawer } = useDrawer()

// State
const captainLoading = ref(false)
const showAssignCaptainDialog = ref(false)
const selectedMember = ref<Member | null>(null)

// Everyone not already a captain of THIS subkrewe. Its own computed so the
// exclusion is not re-evaluated on every keystroke.
const eligibleCaptains = computed(() =>
  membersStore.membersArray.filter(member =>
    !props.subkrewe.captains?.some(captain => captain.id === member.id),
  ),
)

// Filter potential captains
const filterPotentialCaptains = (searchTerm: string) => searchMembers(eligibleCaptains.value, searchTerm)

// Members can't find the SubKrewe to join until a captain has paid or is
// exempt (isSubKreweActive), so each captain's dues stand on their row
const { paradeYear } = useParadeYear()
const duesNotes = computed(() => new Map(
  (props.subkrewe.captains || []).map(captain => [captain.id, duesNote(getMemberDuesStatus(captain), paradeYear.value)]),
))
const duesNoteFor = (captain: Member) => duesNotes.value.get(captain.id) ?? duesNote(getMemberDuesStatus(captain), paradeYear.value)

// View captain details
const viewCaptainDetails = (captain: Member) => {
  if (!captain) return

  setInternalNavigation(true)

  openDrawer({
    title: captain.name || 'Member Details',
    data: captain,
    component: markRaw(AdminMemberDrawerContent),
  })
}

// Remove captain
const removeCaptain = (captain: Member) => {
  if (props.readOnly) return
  if (!captain) return

  confirm.require({
    message: `Remove ${captain.name} as captain of "${props.subkrewe.name}"?`,
    header: 'Remove captain',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Remove captain',
    rejectLabel: 'Cancel',
    accept: async () => {
      try {
        // Store the captain name before removal for the message
        const captainName = captain.name

        // Use store method with full objects
        const success = await subkrewesStore.removeCaptainFromSubKrewe(
          props.subkrewe,
          captain,
          true, // Keep as member by default
        )

        if (success) {
          // Wait for Vue to process all updates
          await nextTick()

          // Reset the captain loading state to trigger template refresh if needed
          captainLoading.value = true
          await nextTick()
          captainLoading.value = false

          toast.add({
            severity: 'success',
            summary: 'Captain Removed',
            detail: `${captainName} is no longer captain of this SubKrewe`,
            life: 3000,
          })

          emit('update', { captains: props.subkrewe.captains })
        }
        else {
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to remove captain',
            life: 3000,
          })
        }
      }
      catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: `${error}`,
          life: 3000,
        })
      }
    },
  })
}

// Assign captain
const selectCaptainAndAssign = async (member: Member) => {
  if (props.readOnly) return
  if (!member) return
  selectedMember.value = member

  // Check if member is already a captain elsewhere
  if (member.member_type === 'captain' && member.subkrewe && member.subkrewe.id !== props.subkrewe.id) {
    // Close the search dialog first so the confirm dialog appears on top
    showAssignCaptainDialog.value = false

    // Wait a moment for the dialog to close before showing confirmation
    await nextTick()

    // Show confirmation dialog
    confirm.require({
      message: `${member.name} is already a captain of "${member.subkrewe.name}".
                Do you want to reassign them to this SubKrewe instead?`,
      header: 'Already a captain',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Move them here',
      rejectLabel: 'Cancel',
      accept: async () => {
        // First remove them from their current SubKrewe
        if (member.subkrewe) {
          const oldSubkrewe = member.subkrewe
          const success1 = await subkrewesStore.removeCaptainFromSubKrewe(
            oldSubkrewe,
            member,
            true, // Keep as member of old subkrewe
          )

          if (success1) {
            // Then assign to this SubKrewe
            await assignCaptainToCurrentSubkrewe(member)
          }
          else {
            toast.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to remove captain from their current SubKrewe',
              life: 3000,
            })
          }
        }
      },
    })
  }
  else {
    // Regular case - assign directly
    await assignCaptainToCurrentSubkrewe(member)
  }
}

const assignCaptainToCurrentSubkrewe = async (member: Member) => {
  try {
    const success = await subkrewesStore.assignCaptainToSubKrewe(
      props.subkrewe,
      member,
    )

    if (success) {
      await nextTick()

      toast.add({
        severity: 'success',
        summary: 'Captain Assigned',
        detail: `${member.name} is now captain of "${props.subkrewe.name}"`,
        life: 3000,
      })

      // Reset UI state
      showAssignCaptainDialog.value = false
      selectedMember.value = null

      emit('update', { captains: props.subkrewe.captains })
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to assign captain',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
}
</script>

<style scoped>
/* The dues mark carries the color; the text stays muted (duesNote) */
.kb-dues-ok { color: var(--kb-accent); }
.kb-dues-warn { color: var(--kb-gold); }
.kb-dues-honor { color: var(--kb-honor); }
.kb-dues-neutral { color: var(--kb-dim); }
</style>
